import i18n from "i18next";
import styles from "../DocumentStamp.module.scss";

export interface LayoutSectionProps {
  /** Read-only mode (no permission). When true, all rows are disabled
   *  even if the preamble emission has shipped — the user has no edit
   *  rights. */
  disabled?: boolean;
}

/**
 * Section 3 of the popover — "Layout".
 *
 * Six rows: Margins, Line spacing, Paragraph indent, Page numbering,
 * Header text, Footer text. Each row is gated on whether Team 1's A1
 * preamble emission has shipped for that field.
 *
 * Round 1: ALL six rows render disabled with the "applies once preamble
 * emission ships" hint. Each Team-1 emission landing flips one flag from
 * `false` to `true` in `EMISSION_LIVE` below — that's the entire diff
 * for the follow-up commit.
 */
const EMISSION_LIVE = {
  margins: false,
  lineSpacing: false,
  paragraphIndent: false,
  pageNumbering: false,
  headerText: false,
  footerText: false,
} as const;

interface LayoutRowSpec {
  key: keyof typeof EMISSION_LIVE;
  label: string;
  /** A short representation of the current value, shown in a `select`-shaped
   *  control. We keep this static (the "default") in round 1 since the
   *  controls are disabled. */
  defaultPlaceholder: string;
  /** True if this row should render as a free-text input rather than a
   *  select. */
  isInput?: boolean;
}

const ROWS: ReadonlyArray<LayoutRowSpec> = [
  { key: "margins", label: i18n.t("editor:stamp.margins"), defaultPlaceholder: i18n.t("editor:stamp.standard25Cm") },
  { key: "lineSpacing", label: i18n.t("editor:stamp.lineSpacing"), defaultPlaceholder: i18n.t("editor:stamp.single10") },
  { key: "paragraphIndent", label: i18n.t("editor:stamp.paragraphIndent"), defaultPlaceholder: "1.5 em" },
  { key: "pageNumbering", label: i18n.t("editor:stamp.pageNumbering"), defaultPlaceholder: i18n.t("editor:stamp.arabicBottomCenter") },
  { key: "headerText", label: i18n.t("editor:stamp.headerText"), defaultPlaceholder: i18n.t("editor:stamp.none"), isInput: true },
  { key: "footerText", label: i18n.t("editor:stamp.footerText"), defaultPlaceholder: i18n.t("editor:stamp.none"), isInput: true },
];

export function Layout({ disabled }: LayoutSectionProps) {
  return (
    <div className={styles.section} data-testid="document-stamp-layout">
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>{i18n.t("editor:stamp.layout")}</span>
        {disabled && <span className={styles.readOnlyPip}>{i18n.t("editor:stamp.readOnly")}</span>}
      </div>

      <div className={disabled ? styles.muted : undefined}>
        {ROWS.map((row) => {
          const isLive = EMISSION_LIVE[row.key];
          const isDisabled = disabled || !isLive;
          return (
            <div key={row.key} className={styles.layoutRow}>
              <span className={styles.layoutLabel}>{row.label}</span>
              <div>
                {row.isInput ? (
                  <input
                    type="text"
                    className={styles.layoutControl}
                    placeholder={row.defaultPlaceholder}
                    disabled={isDisabled}
                    data-testid={`document-stamp-layout-${row.key}`}
                  />
                ) : (
                  <select
                    className={styles.layoutControl}
                    disabled={isDisabled}
                    defaultValue=""
                    data-testid={`document-stamp-layout-${row.key}`}
                  >
                    <option value="">{row.defaultPlaceholder}</option>
                  </select>
                )}
                {!isLive && !disabled && (
                  <span className={styles.layoutHint}>
                    {i18n.t("editor:stamp.appliesOncePreambleEmissionShips")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
